/*
 * Google Maps never frees a map: every google.maps.Map stays referenced by
 * the library for the life of the page, and with it the element it was
 * drawn into and everything that element still hangs from. Measured inside
 * the app's web view, every closed map screen stayed in memory — about 260
 * DOM nodes, 280 listeners and 1 MB per opening, never collected.
 *
 * So maps are made once per kind and lent out. A borrower gets a map drawn
 * in its own element, placed inside the borrower's container, and gives it
 * back on unmount: its listeners come off, the element leaves the page, and
 * the next borrower of that kind reuses both.
 */

type Pooled = { element: HTMLDivElement; map: google.maps.Map };

const free = new Map<string, Pooled[]>();

export type BorrowedMap = {
  map: google.maps.Map;
  /* True when this map was drawn before, so its tiles may already be in. */
  reused: boolean;
  /* addListener / addListenerOnce on the map, removed on release. */
  listen: (event: string, handler: (...args: any[]) => void, once?: boolean) => void;
  /* Back to the pool for the next borrower of this kind. */
  release: () => void;
  /* Never lent again — for a map that failed to draw. */
  discard: () => void;
};

export function borrowMap(
  kind: string,
  container: HTMLElement,
  options: google.maps.MapOptions
): BorrowedMap {
  const maps = window.google.maps;
  const pooled = free.get(kind)?.pop();
  let entry: Pooled;
  if (pooled) {
    entry = pooled;
    entry.map.setOptions(options);
  } else {
    const element = document.createElement("div");
    element.style.width = "100%";
    element.style.height = "100%";
    entry = { element, map: new maps.Map(element, options) };
  }
  container.appendChild(entry.element);

  const listeners: google.maps.MapsEventListener[] = [];
  let done = false;
  const detach = () => {
    if (done) return false;
    done = true;
    listeners.forEach((listener) => listener.remove());
    entry.element.remove();
    return true;
  };

  return {
    map: entry.map,
    reused: !!pooled,
    listen: (event, handler, once = false) => {
      listeners.push(
        once
          ? maps.event.addListenerOnce(entry.map, event, handler)
          : maps.event.addListener(entry.map, event, handler)
      );
    },
    release: () => {
      if (!detach()) return;
      const list = free.get(kind) ?? [];
      list.push(entry);
      free.set(kind, list);
    },
    discard: () => {
      detach();
    },
  };
}

/* After Google Maps is loaded again (MapsLoaderService.reloadMaps), maps
   made by the old copy cannot be lent out. */
export function forgetPooledMaps() {
  free.clear();
}
